import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { lkInvites, ApiError } from "@flame/shared/api";
import { Button, Input, Badge, toast } from "@flame/shared/ui";
import { formatDate } from "@flame/shared/lib";
import { formatRuble } from "@flame/shared/utils";

// ── Static tariff info (shown as context, not a user-selectable override) ────

interface TariffInfo {
  family: "white" | "black";
  tier: "basic" | "pro";
  label: string;
  traffic_gb: number;
  monthly_cost: number;
  description: string;
}

// Values mirror tariffs.tariff_plans in DB. If they drift again, fetch from
// a future /v1/lk/tariffs endpoint instead of hardcoding — the previous
// "500 GB / 299 ₽" Black Pro card vs the real 200 GB / 199 ₽ provisioning
// was a trust-breaker.
const TARIFFS: TariffInfo[] = [
  {
    family: "white",
    tier: "basic",
    label: "White Basic",
    traffic_gb: 50,
    monthly_cost: 149,
    description: "Базовый доступ для личного использования",
  },
  {
    family: "white",
    tier: "pro",
    label: "White Premium",
    traffic_gb: 200,
    monthly_cost: 299,
    description: "Расширенный трафик без ограничений скорости",
  },
  {
    family: "black",
    tier: "basic",
    label: "Black Basic",
    traffic_gb: 50,
    monthly_cost: 99,
    description: "Премиум-доступ с приоритетными серверами",
  },
  {
    family: "black",
    tier: "pro",
    label: "Black Premium",
    traffic_gb: 200,
    monthly_cost: 199,
    description: "Максимальный трафик и скорость без компромиссов",
  },
];

// ── Schema ────────────────────────────────────────────────────────────────────

const schema = z.object({
  code: z
    .string()
    .min(4, "Код не менее 4 символов")
    .max(32, "Код не более 32 символов"),
});
type FormData = z.infer<typeof schema>;

// ── Error mapping ─────────────────────────────────────────────────────────────

function mapError(err: unknown): string {
  if (err instanceof ApiError) {
    switch (err.status) {
      case 404: return "Инвайт-код не найден";
      case 410: return "Инвайт-код больше недействителен";
      case 409: return "Инвайт-код уже использован";
      case 403: return "Аккаунт заблокирован";
      default:  return err.message || "Ошибка сервера";
    }
  }
  return "Неизвестная ошибка";
}

// ── Tariff card (info-only) ───────────────────────────────────────────────────

function TariffCard({ t }: { t: TariffInfo }) {
  const isPro = t.tier === "pro";
  return (
    <div
      className={clsx(
        "flex flex-col gap-2 rounded-xl border p-4 transition-colors",
        isPro
          ? "border-accent/30 bg-[var(--color-accent-muted)]"
          : "border-border bg-surface",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <span
          className={clsx(
            "text-sm font-semibold",
            isPro ? "text-accent" : "text-fg",
          )}
        >
          {t.label}
        </span>
        {isPro && (
          <Badge variant="pro">PRO</Badge>
        )}
      </div>
      <p className="text-xs text-fg-muted">{t.description}</p>
      <div className="mt-1 flex items-baseline justify-between gap-2">
        <span className="text-xs text-fg-subtle">
          {t.traffic_gb} ГБ / мес
        </span>
        <span className="text-sm font-medium text-fg">
          {t.monthly_cost} YY{" "}
          <span className="text-xs font-normal text-fg-muted">
            ≈ {formatRuble(t.monthly_cost)}
          </span>
        </span>
      </div>
    </div>
  );
}

// ── Success state ─────────────────────────────────────────────────────────────

function SuccessView({
  family,
  tier,
  expiresAt,
  onDone,
}: {
  family: string;
  tier: string;
  expiresAt: string;
  onDone: () => void;
}) {
  const familyLabel = family === "black" ? "Black" : "White";
  const tierLabel   = tier   === "pro"   ? "Premium"   : "Basic";

  // Auto-redirect to the dashboard after a short delay so the user sees the
  // "active" confirmation but doesn't have to think about clicking through.
  // Tester feedback (2026-05-12): "после активации не перекинуло на домашний
  // экран" — the manual button alone wasn't obvious enough.
  useEffect(() => {
    const t = setTimeout(onDone, 2500);
    return () => clearTimeout(t);
  }, [onDone]);

  return (
    <div className="flex flex-col items-center gap-5 py-4 text-center">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-[var(--color-success-muted)]">
        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path
            d="M5 12l5 5L19 7"
            stroke="var(--color-success)"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
      <div>
        <p className="text-base font-semibold text-fg">
          Подписка активна
        </p>
        <p className="mt-1 text-sm text-fg-muted">
          {familyLabel} {tierLabel} — действует до {formatDate(expiresAt)}
        </p>
        <p className="mt-2 text-xs text-fg-subtle">
          Открываем кабинет…
        </p>
      </div>
      <Button variant="primary" size="lg" onClick={onDone}>
        Перейти сейчас
      </Button>
    </div>
  );
}

// ── Request-invite CTA ────────────────────────────────────────────────────────

const INVITE_REQUEST_PREFILL =
  "Здравствуйте! У меня нет инвайт-кода. Можно мне получить доступ к FlameVPN?";

/** Tap-to-request: opens TG chat with @AlertVPSV_Bot, message text pre-filled. */
const REQUEST_INVITE_URL =
  `https://t.me/AlertVPSV_Bot?text=${encodeURIComponent(INVITE_REQUEST_PREFILL)}`;

const REQUEST_INVITE_LS_KEY = "lk:invite-requested-at";

function RequestInviteCta({ onRequested }: { onRequested?: () => void }) {
  return (
    <a
      href={REQUEST_INVITE_URL}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => {
        try {
          localStorage.setItem(REQUEST_INVITE_LS_KEY, String(Date.now()));
        } catch {
          // Ignore quota / unsupported storage
        }
        onRequested?.();
      }}
      className={clsx(
        "flex items-center justify-between gap-3 rounded-xl bg-surface p-4 shadow-card",
        "transition-colors hover:bg-surface-elevated",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
      )}
    >
      <div className="flex items-center gap-3 min-w-0">
        <span
          aria-hidden
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-accent-muted)] text-accent"
        >
          <ChatIcon />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-medium text-fg">Запросить инвайт у админа</p>
          <p className="mt-0.5 text-xs text-fg-muted">
            Откроется чат с @AlertVPSV_Bot — текст уже готов, просто отправьте
          </p>
        </div>
      </div>
      <ChevronRightIcon />
    </a>
  );
}

function ChatIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 14 14"
      fill="none"
      aria-hidden
      className="shrink-0 text-fg-muted"
    >
      <path
        d="M5 2l5 5-5 5"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/** Returns true if the user tapped "Request invite" within the last 24h. */
function useRecentlyRequestedInvite(): [boolean, () => void] {
  const [requested, setRequested] = useState(false);

  const recompute = () => {
    try {
      const raw = localStorage.getItem(REQUEST_INVITE_LS_KEY);
      if (!raw) {
        setRequested(false);
        return;
      }
      const ts = Number(raw);
      const fresh = Number.isFinite(ts) && Date.now() - ts < 24 * 60 * 60 * 1000;
      setRequested(fresh);
    } catch {
      setRequested(false);
    }
  };

  useEffect(() => {
    recompute();
  }, []);

  return [requested, recompute];
}

// ── Page ──────────────────────────────────────────────────────────────────────

export function OnboardingPage() {
  const navigate    = useNavigate();
  const queryClient = useQueryClient();

  const [successPayload, setSuccessPayload] = useState<{
    family: string;
    tier: string;
    expiresAt: string;
  } | null>(null);

  const [recentlyRequested, refreshRequestedFlag] = useRecentlyRequestedInvite();

  const {
    register,
    handleSubmit,
    setValue,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ resolver: zodResolver(schema) });

  const onSubmit = async ({ code }: FormData) => {
    try {
      const res = await lkInvites.redeem(code);
      await queryClient.invalidateQueries({ queryKey: ["subs"] });
      toast.success(
        "Инвайт принят",
        `Подписка активирована до ${formatDate(res.expires_at)}`,
      );
      setSuccessPayload({
        family: res.tariff_family,
        tier:   res.tariff_tier,
        expiresAt: res.expires_at,
      });
    } catch (err) {
      setError("code", { message: mapError(err) });
    }
  };

  const handleDone = () => void navigate("/", { replace: true });

  return (
    <div className="mx-auto max-w-sm">
      {/* Back */}
      <button
        type="button"
        onClick={() => void navigate(-1)}
        className="mb-6 flex items-center gap-1.5 text-sm text-fg-muted transition-colors hover:text-fg"
      >
        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden>
          <path
            d="M9 2L4 7l5 5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        Назад
      </button>

      <div className="flex flex-col gap-6">
        {/* Header */}
        <div>
          <h2 className="text-lg font-semibold text-fg">
            Добро пожаловать во FlameVPN
          </h2>
          <p className="mt-1 text-sm text-fg-muted">
            Активируйте инвайт-код, чтобы начать использовать VPN.
          </p>
        </div>

        {successPayload ? (
          <div className="rounded-xl bg-surface p-6 shadow-card">
            <SuccessView
              family={successPayload.family}
              tier={successPayload.tier}
              expiresAt={successPayload.expiresAt}
              onDone={handleDone}
            />
          </div>
        ) : (
          <>
            {/* Invite form */}
            <div className="rounded-xl bg-surface p-5 shadow-card">
              <form
                onSubmit={(e) => void handleSubmit(onSubmit)(e)}
                className="flex flex-col gap-4"
              >
                <Input
                  label="Инвайт-код"
                  placeholder="XXXX-XXXX-XXXX"
                  autoCapitalize="characters"
                  autoComplete="off"
                  spellCheck={false}
                  error={errors.code?.message}
                  {...register("code", {
                    onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
                      setValue("code", e.target.value.trim().toUpperCase(), {
                        shouldValidate: false,
                      });
                    },
                  })}
                />
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                  className="w-full"
                  loading={isSubmitting}
                >
                  Активировать
                </Button>
              </form>
            </div>

            {/* Request invite CTA */}
            <RequestInviteCta onRequested={refreshRequestedFlag} />

            {/* Tariff info — hidden for 24h after the user requested an invite */}
            {recentlyRequested ? (
              <p className="text-center text-xs text-fg-subtle">
                Запрос отправлен — ждите ответ в Telegram.
              </p>
            ) : (
              <section aria-label="Тарифы">
                <p className="mb-3 text-xs font-medium uppercase tracking-wide text-fg-muted">
                  Доступные планы
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {TARIFFS.map((t) => (
                    <TariffCard key={`${t.family}-${t.tier}`} t={t} />
                  ))}
                </div>
                <p className="mt-3 text-xs text-fg-subtle">
                  Тариф определяется инвайт-кодом. Отображение носит
                  информационный характер.
                </p>
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
}
